import { useRef, useState } from 'react';

import { IconButton } from 'design-system';
import { GripVertical, Move, X } from 'lucide-react';

import { TABNAME_MAX_LENGTH } from '@/config/name';
import { DashboardNameInput } from '@/features/dashboard/components/DashboardNameInput';
import { selectDraftTabNames } from '@/features/dashboard/state/dashboardEdit.selectors';
import { getTabNameError } from '@/features/dashboard/state/lib/invariants';
import { useAppSelector } from '@/store/hooks';

import MoveTabToDashboardMenu from './MoveTabToDashboardMenu';

export type EditableTabLabelProps = {
  allowTabReorder: boolean;
  error?: string;
  name: string;
  tabId: string;
  onRename: (tabId: string, value: string) => void;
  onDelete: (tabId: string) => void;
  canDelete: boolean;
  /** Whether there are other dashboards to move to */
  canMove?: boolean;
  /** Ref forwarded to the drag handle element, for use with @dnd-kit sortable */
  dragHandleRef?: React.RefObject<HTMLDivElement | null>;
};

export const EditableTabLabel = ({
  allowTabReorder,
  error: errorProp,
  name,
  tabId,
  onRename,
  onDelete,
  canDelete,
  canMove = true,
  dragHandleRef
}: EditableTabLabelProps) => {
  const [moveMenuOpen, setMoveMenuOpen] = useState(false);
  const moveButtonRef = useRef<HTMLButtonElement>(null);

  const draftTabNames = useAppSelector(selectDraftTabNames);
  const otherNames = draftTabNames.filter(n => n !== name);
  const validationError = getTabNameError(name, otherNames);
  const error = errorProp || validationError;
  return (
    <div className='-mx-2 flex w-[calc(100%+16px)] items-center'>
      {allowTabReorder && (
        <div
          ref={dragHandleRef}
          aria-label='Drag to reorder tab'
          className='tab-drag-handle flex h-12 w-4 shrink-0 cursor-grab items-center justify-center bg-response'
          title='Drag to reorder tab'
        >
          <GripVertical className='size-3.5' />
        </div>
      )}
      <div
        className='w-[100px] min-w-[100px] flex-1 [&_input]:h-[46px]'
        data-tab-drag-disabled='true'
      >
        <DashboardNameInput
          ariaLabel='Tab name'
          error={error}
          maxLength={TABNAME_MAX_LENGTH}
          value={name}
          onChange={value => {
            onRename(tabId, value);
          }}
        />
      </div>
      {canMove && (
        <IconButton
          ref={moveButtonRef}
          className='mr-0.5 h-[46px] rounded-none'
          label='Move to ...'
          variant='ghost'
          onClick={e => {
            e.stopPropagation();
            setMoveMenuOpen(true);
          }}
        >
          <Move aria-hidden='true' />
        </IconButton>
      )}
      {canDelete && (
        <IconButton
          className='h-[46px] rounded-none'
          label='Remove tab'
          variant='ghost'
          onClick={e => {
            e.stopPropagation();
            onDelete(tabId);
          }}
        >
          <X aria-hidden='true' />
        </IconButton>
      )}
      <MoveTabToDashboardMenu
        // oxlint-disable-next-line react-hooks-js/refs
        anchorEl={moveButtonRef.current}
        disableMove={!canDelete}
        open={moveMenuOpen}
        tabId={tabId}
        tabName={name}
        onClose={() => {
          setMoveMenuOpen(false);
        }}
        onMoved={() => {
          // The context handles tab removal from tabDrafts
          // and currentTabId fallback
        }}
        onRemoveTab={onDelete}
      />
    </div>
  );
};
